import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { fetchIndeedGraphQL } from 'shared/legacy/hooks/useBrowserExtension/indeed/utils';
import { MessageReceivedPlatform } from 'shared/legacy/hooks/useBrowserExtension/platform.types';
import { findMessagesByConversationGQLQuery } from './indeed.GQLOperations';
import {
  buildMessagesBasedOnConversationsIdProps,
  IndeedCandidateMessagesResponse,
  IndeedConversationAuthorRole,
  IndeedEnhancedConfig,
  IndeedMessage,
} from './indeed.types';
import { extractJobInfo } from './indeed.utils';

export const buildMessagesBasedOnConversationsId = async ({
  indeedConfig,
  conversationsId,
  licenseEmailAddress,
  candidateId,
  candidateName,
  tenantId,
  userEmail,
}: buildMessagesBasedOnConversationsIdProps) => {
  if (licenseEmailAddress == null || userEmail == null) {
    newrelic.addPageAction('IndeedSyncStep', {
      step: 'messages_skipped',
      candidateId,
      hasLicenseEmail: String(licenseEmailAddress != null),
      hasUserEmail: String(userEmail != null),
      conversationCount: conversationsId.length,
    });
    return [];
  }

  try {
    const promises = conversationsId.map((conversationId) =>
      getRecruiterMessagesByConversationId({
        indeedConfig,
        conversationId,
        licenseEmailAddress,
        tenantId,
        candidateId,
      }),
    );

    const indeedMessagesPromises = await Promise.all(promises);

    const indeedMessages: IndeedMessage[] = indeedMessagesPromises.flat();

    if (indeedMessages.length > 0) {
      await notificationsClient.fallbackSaveMessages({
        messages: indeedMessages,
        credentialUsername: userEmail,
        platform: MessageReceivedPlatform.INDEED,
        candidateId: candidateId,
        candidateName: candidateName,
      });

      return indeedMessages;
    } else {
      return [];
    }
  } catch (err) {
    newrelic.addPageAction('IndeedSyncStep', {
      step: 'messages_build_error',
      candidateId,
      error: String(err),
      conversationCount: conversationsId.length,
    });
    return [];
  }
};

export const getRecruiterMessagesByConversationId = async ({
  indeedConfig,
  conversationId,
  licenseEmailAddress,
  tenantId,
  candidateId,
}: {
  indeedConfig: IndeedEnhancedConfig;
  conversationId: string;
  licenseEmailAddress: string | null;
  tenantId: number;
  candidateId: string;
}): Promise<IndeedMessage[]> => {
  const messages: IndeedMessage[] = [];
  const messageIds: string[] = [];
  const errorsArray: unknown[] = [];
  let hasMoreMessages: boolean;
  let previousPageToken: string | undefined;
  hasMoreMessages = true;

  while (hasMoreMessages && errorsArray.length <= 2) {
    try {
      const response: IndeedCandidateMessagesResponse = await fetchIndeedGraphQL(
        findMessagesByConversationGQLQuery(conversationId, previousPageToken),
        indeedConfig,
      );

      const eventsConnection = response.data.conversation.eventsConnection;

      const hasPreviousPage = eventsConnection.pageInfo.hasPreviousPage;

      newrelic.addPageAction('IndeedSyncStep', {
        step: 'messages_page',
        tenantId,
        candidateId,
        conversationId,
        hasPreviousPage: String(hasPreviousPage),
        edgeCount: eventsConnection.edges.length,
        messagesSoFar: messages.length,
        pageToken: previousPageToken ?? '',
      });

      if (hasPreviousPage) {
        previousPageToken = eventsConnection.pageInfo.startCursor;
      } else {
        hasMoreMessages = false;
      }

      eventsConnection.edges.map((edge) => {
        const conversationNode = edge.node;
        const isEmployerAuthor = conversationNode.author.role == IndeedConversationAuthorRole.EMPLOYER;

        if (!messageIds.includes(conversationNode.id)) {
          messages.push({
            id: conversationNode.id,
            senderId: conversationNode.author.accountKey,
            content: conversationNode.messageBody,
            fromSelf: isEmployerAuthor,
            sentAt: new Date(conversationNode.publicationDateTime).getTime(),
            conversationId: conversationId,
            licenseEmailAddress: licenseEmailAddress,
            type: conversationNode.type,
            jobInfo: extractJobInfo(edge),
          });

          messageIds.push(conversationNode.id);
        }
      });
    } catch (err) {
      errorsArray.push(err);
    }
  }
  return messages;
};
